import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import {
  User,
  Policy,
  Onboarding,
  OnboardingQuestion,
  OnboardingQuestionType,
} from '@core/generated/gql-types';
import { downloadFileFromUrl } from '@/utils/download.util';

export interface WorkerPoliciesSectionMeta {
  filterByOnboarding: boolean;
  onboardingAgreeToPolicyStepCount: number;
}

interface WorkerPoliciesSectionProps {
  worker: User;
  policies: Policy[];
  policySectionMeta: WorkerPoliciesSectionMeta;
  onPreviewPolicy: (policy: Policy) => void;
}

const formatPolicyTypeLabel = (policyType: string): string => {
  if (!policyType) return 'Policy';
  return policyType
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

const formatPolicyAgreedAt = (agreedAt: string): string => {
  if (!agreedAt) return '';
  try {
    const date = new Date(
      /^\d+$/.test(agreedAt) ? parseInt(agreedAt, 10) : agreedAt,
    );
    if (isNaN(date.getTime())) return agreedAt;
    return format(date, "d MMM yyyy 'at' HH:mm");
  } catch {
    return agreedAt;
  }
};

const workerDisplayName = (worker: User): string => {
  const name = [worker.firstName, worker.lastName].filter(Boolean).join(' ');
  return name.trim() || worker.email || worker.id;
};

/** Policy types referenced by AGREE_TO_POLICY steps in onboarding (recursive). */
export function getOnboardingAgreeToPolicyFilter(
  onboarding: Onboarding | null | undefined,
): { filter: boolean; types: Set<string> } {
  const pages = onboarding?.onboardingJSON?.pages;
  if (!pages?.length) {
    return { filter: false, types: new Set() };
  }
  const types = new Set<string>();
  const walk = (question: OnboardingQuestion) => {
    if (
      question.type === OnboardingQuestionType.AgreeToPolicy &&
      question.key
    ) {
      types.add(question.key);
    }
    question.childQuestions?.forEach(walk);
  };
  for (const page of pages) {
    page.questions?.forEach(walk);
  }
  return { filter: true, types };
}

const WorkerPoliciesSection: React.FC<WorkerPoliciesSectionProps> = ({
  worker,
  policies,
  policySectionMeta,
  onPreviewPolicy,
}) => {
  const getPolicyDownloadFilename = useCallback(
    (policy: Policy): string => {
      const namePart = workerDisplayName(worker).replace(/\s+/g, '-') || 'user';
      const typePart = (policy.policyType || 'policy').replace(/\s+/g, '-');
      const datePart = policy.agreedAt
        ? format(
            new Date(
              /^\d+$/.test(policy.agreedAt)
                ? parseInt(policy.agreedAt, 10)
                : policy.agreedAt,
            ),
            'yyyy-MM-dd',
          )
        : policy.id;
      return `policy-${typePart}-${namePart}-${datePart}.pdf`;
    },
    [worker],
  );

  const handleDownloadPolicy = useCallback(
    async (policy: Policy) => {
      if (!policy.pdfUrl) return;
      downloadFileFromUrl(policy.pdfUrl, getPolicyDownloadFilename(policy));
    },
    [getPolicyDownloadFilename],
  );

  let policiesEmptyMessage: string;
  if (!policySectionMeta.filterByOnboarding) {
    policiesEmptyMessage = 'No policies are configured for this tenant yet.';
  } else if (policySectionMeta.onboardingAgreeToPolicyStepCount === 0) {
    policiesEmptyMessage =
      "This worker's onboarding does not include any policy acknowledgement steps.";
  } else {
    policiesEmptyMessage =
      "No policy records match the policy steps in this worker's onboarding.";
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">Policies</h3>
      {policies.length === 0 ? (
        <div className="py-4 text-sm text-gray-500">{policiesEmptyMessage}</div>
      ) : (
        <div className="space-y-4">
          {policies.map((policy) => (
            <div
              key={policy.id}
              className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-b-0"
            >
              <div className="flex-1 pr-4">
                <div className="text-sm font-medium text-gray-900">
                  {formatPolicyTypeLabel(policy.policyType)}
                  {policy.isAgreedTo ? (
                    <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      Acknowledged
                    </span>
                  ) : (
                    <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      Not acknowledged
                    </span>
                  )}
                </div>
                {policy.isAgreedTo && (
                  <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                    {policy.agreedAt && (
                      <div>
                        Acknowledged on {formatPolicyAgreedAt(policy.agreedAt)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => onPreviewPolicy(policy)}
                  className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
                  View
                </button>
                {policy.isAgreedTo && policy.pdfUrl ? (
                  <button
                    type="button"
                    onClick={() => void handleDownloadPolicy(policy)}
                    className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                    Download
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkerPoliciesSection;
